import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import LoadingIndicator from "@/components/LoadingIndicator/LoadingIndicator";
import { fetchEmailCampaignActivityLogDetail } from "@/modules/emailManagement/features/EmailEventLogsManagement/api/fetchEmailCampaignActivityLogDetail";
import { EmailActivityLogDetail } from "@/modules/emailManagement/features/EmailEventLogsManagement/api/fetchEmailCampaignActivityLogDetail/types";
import { formatDate } from "@/utils/dateUtils";

interface EmailActivityLogDetailModalProps {
  messageId: string | null;
  onClose: () => void;
}

const EmailActivityLogDetailModal: React.FC<
  EmailActivityLogDetailModalProps
> = ({ messageId, onClose }) => {
  const [detail, setDetail] = useState<EmailActivityLogDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!messageId) {
      setDetail(null);
      setLoading(false);
      return;
    }

    let isCancelled = false;

    // Reset previous detail while loading a new one to avoid showing stale data
    setDetail(null);
    setLoading(true);

    fetchEmailCampaignActivityLogDetail(messageId)
      .then((response) => {
        if (!isCancelled) {
          setDetail(response.data);
        }
      })
      .catch(() => {
        if (!isCancelled) {
          // Ensure detail is cleared on error to avoid showing stale data
          setDetail(null);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setLoading(false);
        }
      });

    return () => {
      isCancelled = true;
    };
  }, [messageId]);

  return (
    <Dialog className="relative z-50" onClose={onClose} open={!!messageId}>
      <DialogBackdrop className="fixed inset-0 bg-black/30" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="bg-white dark:bg-slate-800 rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-slate-700 shrink-0">
            <DialogTitle className="text-base font-normal text-gray-900 dark:text-white">
              {detail?.subject ?? "Message Details"}
            </DialogTitle>
            <button
              aria-label="Close"
              className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              onClick={onClose}
              type="button"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="overflow-y-auto flex-1 px-6 py-4 space-y-6">
            {loading && <LoadingIndicator isFullScreen={false} />}

            {!loading && detail && (
              <>
                {/* Summary */}
                <dl
                  className="grid text-sm gap-y-2"
                  style={{ gridTemplateColumns: "8rem 1fr" }}
                >
                  <dt className="text-gray-500 dark:text-gray-400">To</dt>
                  <dd className="text-gray-900 dark:text-white">
                    {detail.email ?? ""}
                  </dd>

                  <dt className="text-gray-500 dark:text-gray-400">Subject</dt>
                  <dd className="text-gray-900 dark:text-white">
                    {detail.subject ?? ""}
                  </dd>

                  <dt className="text-gray-500 dark:text-gray-400">Sent At</dt>
                  <dd className="text-gray-900 dark:text-white">
                    {detail.ts
                      ? formatDate(new Date(detail.ts * 1000).toISOString())
                      : ""}
                  </dd>

                  <dt className="text-gray-500 dark:text-gray-400">Status</dt>
                  <dd className="text-gray-900 dark:text-white capitalize">
                    {detail.state ?? ""}
                  </dd>

                  <dt className="text-gray-500 dark:text-gray-400">Opens</dt>
                  <dd className="text-gray-900 dark:text-white">
                    {detail.opens ?? 0}
                  </dd>

                  {detail.template && (
                    <>
                      <dt className="text-gray-500 dark:text-gray-400">
                        Template
                      </dt>
                      <dd className="text-gray-900 dark:text-white">
                        {detail.template}
                      </dd>
                    </>
                  )}

                  <dt className="text-gray-500 dark:text-gray-400">
                    Message ID
                  </dt>
                  <dd className="text-gray-400 dark:text-gray-500 font-mono text-xs break-all">
                    {detail._id}
                  </dd>
                </dl>

                {/* Opens */}
                {(detail.opens_detail?.length ?? 0) > 0 && (
                  <div>
                    <h3 className="text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                      Opens
                    </h3>
                    <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                          <tr className="text-left">
                            <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              Time
                            </th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              Location
                            </th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              Client
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                          {detail.opens_detail!.map((open, i) => (
                            <tr
                              className="hover:bg-gray-50 dark:hover:bg-slate-700/50"
                              key={i}
                            >
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">
                                {formatDate(
                                  new Date(open.ts * 1000).toISOString(),
                                )}
                              </td>
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300 text-xs">
                                {open.location ?? ""}
                              </td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400 text-xs">
                                {open.ua ?? ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Clicks */}
                {(detail.clicks_detail?.length ?? 0) > 0 && (
                  <div>
                    <h3 className="text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                      Clicks
                    </h3>
                    <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                          <tr className="text-left">
                            <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              Time
                            </th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              URL
                            </th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              Location
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                          {detail.clicks_detail!.map((click, i) => (
                            <tr
                              className="hover:bg-gray-50 dark:hover:bg-slate-700/50"
                              key={i}
                            >
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">
                                {formatDate(
                                  new Date(click.ts * 1000).toISOString(),
                                )}
                              </td>
                              <td className="px-4 py-2 text-gray-500 dark:text-gray-400 text-xs break-all">
                                {click.url ?? ""}
                              </td>
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300 text-xs">
                                {click.location ?? ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Message Body */}
                {(detail.html || detail.text) && (
                  <div>
                    <h3 className="text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                      Message Body
                    </h3>
                    <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                      {detail.html ? (
                        <iframe
                          className="w-full"
                          onLoad={(e) => {
                            const iframe = e.target as HTMLIFrameElement;
                            if (iframe.contentDocument?.body) {
                              iframe.style.height =
                                iframe.contentDocument.body.scrollHeight +
                                32 +
                                "px";
                            }
                          }}
                          sandbox="allow-same-origin"
                          srcDoc={detail.html}
                          style={{ minHeight: "400px", border: "none" }}
                        />
                      ) : (
                        <pre className="p-4 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-words">
                          {detail.text}
                        </pre>
                      )}
                    </div>
                  </div>
                )}

                {/* SMTP Events */}
                {(detail.smtp_events?.length ?? 0) > 0 && (
                  <div>
                    <h3 className="text-sm font-normal text-gray-700 dark:text-gray-300 mb-2">
                      SMTP Events
                    </h3>
                    <div className="rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-slate-700 border-b border-gray-200 dark:border-slate-600">
                          <tr className="text-left">
                            <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              Time
                            </th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              Type
                            </th>
                            <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                              Diagnostic
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                          {detail.smtp_events!.map((event, i) => (
                            <tr
                              className="hover:bg-gray-50 dark:hover:bg-slate-700/50"
                              key={i}
                            >
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap text-xs">
                                {formatDate(
                                  new Date(event.ts * 1000).toISOString(),
                                )}
                              </td>
                              <td className="px-4 py-2 text-gray-700 dark:text-gray-300 text-xs">
                                {event.type ?? ""}
                              </td>
                              <td className="px-4 py-2 text-gray-400 dark:text-gray-500 font-mono text-xs">
                                {event.diag ?? ""}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

export default EmailActivityLogDetailModal;
